import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class SdkManagerService {
  private utilService = inject(UtilService);
  private router = inject(Router);

  private get activeSession(): string | null {
    return sessionStorage.getItem('sdkSessionId');
  }

  private set activeSession(sessionId: string | null) {
    if (sessionId) {
      sessionStorage.setItem('sdkSessionId', sessionId);
    } else {
      sessionStorage.removeItem('sdkSessionId');
    }
  }

  private get isIframeMode(): boolean {
    return sessionStorage.getItem('sdkIsIframe') === 'true';
  }

  private set isIframeMode(isIframe: boolean) {
    if (isIframe) {
      sessionStorage.setItem('sdkIsIframe', 'true');
    } else {
      sessionStorage.removeItem('sdkIsIframe');
    }
  }

  // Use activeSession as the single source of truth for both session ID and parent origin
  // Since in the context of this app, they represent the same value (embedding site's origin)
  private get parentOrigin(): string | null {
    return this.activeSession;
  }

  // Get the parent window reference based on the stored iframe mode
  private get parentWindow(): Window | null {
    if (!this.isSessionActive) {
      return null;
    }
    return this.isIframeMode ? window.parent : (window.opener || window.parent);
  }

  public get isSessionActive(): boolean {
    return this.activeSession !== null;
  }

  constructor() {
  }

  initializeSession(sessionId: string): void {
    this.activeSession = sessionId;
    // parentOrigin is now derived from activeSession via getter - no need for separate assignment
    console.log(`SDK session initialized with ID: ${sessionId}`);
  }

  setWindowMode(isIframe: boolean): void {
    this.isIframeMode = isIframe;
    console.log(`SDK window mode set - iframe: ${isIframe}`);
  }


  messageAndTerminateSession(message: {status: 'SUCCESS' | 'FAILURE', message: string}, showPopup = true): void {
    if (this.activeSession) {
      console.log(`Terminating SDK session with ID: ${this.activeSession}`);
      // Logic to terminate the session goes here
      if (message) {
        if (showPopup) {
          if (message.status === 'SUCCESS') {
            this.utilService.showSuccessMessage('Success', 'Your consent has been shared successfully.');
          } else if (message.status === 'FAILURE') {
            this.utilService.showErrorMessage('Failure', 'Your consent has not been shared with the requestor.');
          }
        }

        const canRedirect = this.parentOrigin === 'redirect';
        if (canRedirect) {
          // If parentOrigin is 'redirect', we assume a full-page redirect is desired
          // Redirect to full-page result view (mobile-friendly), passing both state and query params
          this.router.navigate(['/sdk-result'], {
            queryParams: {
              status: message.status,
              message: message.message
            },
            state: { sdkMessage: message }
          });
        } else {
          if (this.parentWindow) {
            // Use the known parent origin captured during initialization to avoid cross-origin access
            const targetOrigin = this.parentOrigin || '*';
            this.parentWindow.postMessage(message, targetOrigin);
          }
        }
      }

      this.activeSession = null;
      this.isIframeMode = false;
    } else {
      console.warn('No active SDK session to terminate.');
    }
  }
}
